import { useDebounce } from 'usehooks-ts';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import AreaInput from 'components/area-input';
import { ButtonProps } from 'components/button';
import BaseScreen from 'components/base-screen';
import ProgressChip from 'components/progress-chip';
import MeaningfulHeader from 'components/meaningful-header';

import useBoundStore from 'store';
import { formatArea } from 'lib/utils';
import { RangeNumber } from 'lib/types';
import { SQ_METER_SIGN } from 'lib/constants';
import { BuyApartmentFlowPaths } from 'routes';
import useViewApartmentDealsButtonProps from 'hooks/use-view-apartment-deals-button-props';
import useApartmentsFilterLimitsOnboarding from 'api/hooks/use-apartment-filter-limits-onboarding';

import styles from './styles.module.scss';

const ChooseApartmentSquareFootage: React.FC = () => {
  const navigate = useNavigate();

  const areaSqFootageRange = useBoundStore((s) => s.areaSqFootageRange);
  const setAreaSqFootageRange = useBoundStore((s) => s.setAreaSqFootageRange);

  const [lowerSqFootage, setLowerSqFootage] = useState(areaSqFootageRange.min);
  const [upperSqFootage, setUpperSqFootage] = useState(areaSqFootageRange.max);
  const [rangeError, setRangeError] = useState(false);

  const debouncedLowerSqFootage = useDebounce(lowerSqFootage);
  const debouncedUpperSqFootage = useDebounce(upperSqFootage);

  useEffect(() => {
    let errored = false;
    const range: RangeNumber = {};

    if (debouncedLowerSqFootage) {
      range.min = Number(debouncedLowerSqFootage);
    }

    if (debouncedUpperSqFootage) {
      range.max = Number(debouncedUpperSqFootage);
      if (range.min && range.min > range.max) {
        errored = true;
        setRangeError(true);
      }
    }

    if (!errored) {
      setRangeError(false);
      setAreaSqFootageRange(range);
    }
  }, [debouncedLowerSqFootage, debouncedUpperSqFootage, setAreaSqFootageRange]);

  const { filterLimits } = useApartmentsFilterLimitsOnboarding();

  const buttons: ButtonProps[] = [
    useViewApartmentDealsButtonProps(),
    {
      label: 'Продолжить',
      onClick: () => navigate(BuyApartmentFlowPaths.choosePriceRange),
    },
  ];

  return (
    <BaseScreen
      padContent
      buttons={buttons}
      title='Подбор объектов'
      onBackButtonClick={() => navigate(BuyApartmentFlowPaths.chooseApartmentPlan)}
    >
      <div className={styles.container}>
        <MeaningfulHeader
          className={styles.header}
          header={`Общая площадь, ${SQ_METER_SIGN}`}
          motto='Дом - это там, где находится сердце'
        />

        <AreaInput
          clearButton
          value={lowerSqFootage}
          onChange={(value) => {
            if (value !== '') {
              setLowerSqFootage(Number(value));
              return;
            }
            setLowerSqFootage(undefined);
          }}
          autoFocus={lowerSqFootage === undefined}
          placeholder={formatArea(filterLimits?.areaMin ?? 0)}
          error={rangeError ? 'Мин. площадь должна быть меньше макс. площади' : ''}
        />
        <AreaInput
          clearButton
          value={upperSqFootage}
          onChange={(value) => {
            if (value !== '') {
              setUpperSqFootage(Number(value));
              return;
            }
            setUpperSqFootage(undefined);
          }}
          placeholder={formatArea(filterLimits?.areaMax ?? 0)}
          error={rangeError ? 'Макс. площадь должна быть больше мин. площади' : ''}
        />
      </div>
    </BaseScreen>
  );
};

export default ChooseApartmentSquareFootage;
